import { useState, useRef, useEffect } from 'react';
import MicIcon from '../../../assets/images/mic.png';
import AdelIcon from '../../../assets/icons/adel-circle-icon.png';
import NoorIcon from '../../../assets/icons/noor-circle-icon.png';
import AdelAvatar from '../../../assets/images/adel-avatar.png';
import NoorAvatar from '../../../assets/images/noor-avatar.png';
import SendIcon from '../../../assets/images/send-icon.png';

import {
  Box,
  Flex,
  Input,
  Button,
  Text,
  Image,
  useMediaQuery,
  Center,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Message } from '../../../models/chat';
import {
  fetchConversation,
  initialMessage,
  sendMessage,
} from '../../../clients/be';
import { Trans, useTranslation } from 'react-i18next';
import LanguageSelector from '../languageSelector/languageSelector';
import { PauseIcon } from 'lucide-react';
import VoiceWaveForm from '../../voice-chat/VoiceWaveForm';
import BackButton from '../../common/BackButton';

const OpenAI = ({
  conversationId,
  openVoiceChat,
  language = 'AR',
  setHideLanguageSwitch,
  setSelectedVoice,
}: {
  openVoiceChat: () => void;
  conversationId: string;
  language: 'EN' | 'AR';
  setHideLanguageSwitch: (value: boolean) => void;
  setSelectedVoice: (value: string) => void;
}) => {
  const { t } = useTranslation();
  const [isListening, setIsListening] = useState(false);

  const audioChunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  const isIOS = useMediaQuery('(pointer: fine)');
  const client = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const chatRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTextLoading, setIsTextLoading] = useState<boolean>(false);
  const [isSTTLoading, setIsSTTLoading] = useState(false);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const fetchInitialMessage = async () => {
      try {
        await initialMessage(conversationId);
        // Optionally handle the response or update your state here
      } catch (error) {
        console.error('Failed to fetch initial message:', error);
      }
    };

    fetchInitialMessage();
  }, [conversationId]);

  const sendMutation = useMutation({
    mutationFn: async ({ conversationId, textMessage }: any) => {
      await sendMessage(conversationId, textMessage, (chunk: string) => {
        // Handle incoming chunks
        if (typeof chunk === 'string') {
          setMessages((prevMessages) => {
            // Check if there are messages
            if (prevMessages.length > 0) {
              // Update the last message
              const lastMessageIndex = prevMessages.length - 1;
              const lastMessage = prevMessages[lastMessageIndex];

              // Check if the last message is from the recipient
              if (lastMessage.role === 0) {
                // If the last message is from the recipient, create a new sender message
                return [
                  ...prevMessages,
                  {
                    role: 1, // Sender
                    message: chunk,
                    timestamp: new Date().toISOString(),
                  },
                ];
              } else {
                // If the last message is from the sender, append chunk to it
                return [
                  ...prevMessages.slice(0, lastMessageIndex),
                  {
                    ...lastMessage,
                    message: lastMessage.message + chunk, // Append chunk to the last message
                  },
                ];
              }
            } else {
              // If no messages exist, create a new one
              return [
                {
                  role: 1, // Sender
                  message: chunk,
                  timestamp: new Date().toISOString(),
                },
              ];
            }
          });
        }
      });
    },
    mutationKey: ['send-message'],
    onSuccess: (data) => {
      console.log('Received data:', data);
      // Optionally handle the final message or response here
    },
    onMutate: () => {
      setIsTextLoading(true);
    },
    onSettled: () => {
      setIsTextLoading(false);
    },
  });

  const handleSendMessage = (conversationId: string, textMessage: string) => {
    sendMutation.mutate({ conversationId, textMessage });
  };

  const startListening = async () => {
    setInputValue('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        audioChunksRef.current.push(event.data);
      });
      mediaRecorderRef.current.addEventListener('stop', () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/mp3; codecs=opus',
        });

        audioChunksRef.current = [];

        setIsSTTLoading(true);

        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');

        fetch(
          `${process.env.REACT_APP_API_URL}/api/converter/stt?UserTrackedId=${conversationId}`,
          {
            method: 'POST',
            body: formData,
          }
        )
          .then((response) => response.json())
          .then((data) => {
            const transcription = data.transcription;

            setInputValue(transcription);
          })
          .catch((error) => {
            console.error('Error sending audio to API:', error);
          })
          .finally(() => {
            setIsSTTLoading(false);
          });
      });
      mediaRecorderRef.current.start();
      setIsListening(true);
    } catch (error) {
      alert('Error accessing microphone. Please check your audio permissions.');
      console.error('Error starting audio recording:', error);
    }
  };

  const stopListening = () => {
    setIsListening(false);

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      console.log('stopped');
    }
  };

  return (
    <Flex h="full" w="full" pos="relative">
      <Box borderRadius="lg" w="full">
        {messages.length !== 0 && (
          <BackButton
            onClick={() => {
              setMessages([]);
            }}
            btnText={t('backToHome')}
            isArabic={language === 'AR' ? true : false}
          />
        )}
        <Flex
          direction="column"
          h={{ base: 'calc(79vh - 13em)', md: '72vh' }}
          ref={chatRef}
          fontSize="11px"
          pos="relative"
          pb={{
            base: messages.length !== 0 ? '3rem' : '0rem',
            md: messages.length !== 0 ? '3rem' : '0rem',
          }}
          mt={{ base: '3rem', md: '1rem' }}
          overflow="auto"
        >
          {messages.map((message, idx) => (
            <Flex
              key={idx}
              direction={message.role === 0 ? 'row-reverse' : 'row'}
              align="start"
              mb={4}
            >
              {message.role !== 0 && (
                <Image src="./avatar-logo.png" me={2} w="25px" />
              )}
              <Box
                bg={message.role === 0 ? '#F2F2F5' : ''}
                color="black"
                borderRadius="md"
                borderTopEndRadius="0"
                px={message.role === 0 ? 3 : 0}
                py={1}
                maxW={message.role === 0 ? '80%' : '100%'}
              >
                <Text whiteSpace="pre-line">{message.message}</Text>
              </Box>
            </Flex>
          ))}
          {isTextLoading && (
            <Flex direction="row" align="start" mb={4}>
              <Image src="./avatar-logo.png" me={2} w="25px" />

              <Box py={1}>
                <Box className="loading-spinner">
                  <motion.div
                    className="dot"
                    animate={{
                      scale: [1, 2, 1],
                      opacity: [1, 0, 1],
                      borderRadius: ['50%', '50%', '50%'],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      repeatType: 'loop',
                      ease: 'linear',
                    }}
                  />
                </Box>
              </Box>
            </Flex>
          )}
          {/* Initial screen */}
          {messages.length === 0 && !isTextLoading && (
            <Flex
              flexDir="column"
              justifyContent="space-between"
              w="full"
              height="full"
              pt="3"
              pb="0"
            >
              {/* Banner text */}
              <Flex
                pos="absolute"
                top={{ base: '15%', md: '18%' }}
                w="100%"
                transform="translateY(-40%)"
                alignItems="center"
                justify="center"
              >
                <Center w="full" flexDir="column">
                  <Flex>
                    <Text
                      fontSize={{ base: '1rem', md: '2xl' }}
                      mb="2"
                      fontWeight="bold"
                    >
                      {t('adelGreet')}
                    </Text>
                  </Flex>
                  <Text
                    fontSize={{ base: '0.8rem', md: '1.1rem' }}
                    color="#6B7584"
                    mb="2rem"
                    textAlign="center"
                  >
                    {t('adelIntro')}
                  </Text>
                </Center>
              </Flex>

              {/* BOT options for Adel and Noor */}
              <Flex
                pos="absolute"
                left="30%"
                top="60%"
                transform="translate(-30%, -40%)"
                w="full"
                justifyContent="center"
                gap="3"
              >
                {/* talk to Adel */}
                <Center flexDir="column" mt={5} gap=".5rem">
                  <Button
                    bg="none"
                    w="100%"
                    h="auto"
                    _hover={{
                      opacity: '0.9',
                    }}
                    _active={{
                      bg: 'none',
                    }}
                    onClick={() => {
                      openVoiceChat();
                      setSelectedVoice('3');
                    }}
                    flexDir="column"
                    textAlign="center"
                    p="0"
                  >
                    <Image
                      src={AdelAvatar}
                      maxW={{ base: '10rem', md: '18rem' }}
                    />

                    <Flex gap=".3rem" mt="2">
                      <Text
                        fontWeight="semibold"
                        fontSize={{ base: 'lg', md: '2xl' }}
                      >
                        {t('talkTo')}
                      </Text>
                      <Text
                        fontFamily="Blenda Script"
                        fontSize={{ base: 'lg', md: '2xl' }}
                      >
                        {t('adel')}
                      </Text>
                    </Flex>
                  </Button>
                </Center>
                {/* talk to Noor */}
                <Center flexDir="column" mt={5} gap=".5rem">
                  <Button
                    bg="none"
                    w="100%"
                    h="auto"
                    _hover={{
                      opacity: '0.9',
                    }}
                    _active={{
                      bg: 'none',
                    }}
                    onClick={() => {
                      openVoiceChat();
                      setSelectedVoice('5');
                    }}
                    flexDir="column"
                    textAlign="center"
                    p="0"
                  >
                    <Image
                      src={NoorAvatar}
                      maxW={{ base: '10rem', md: '18rem' }}
                    />

                    <Flex gap=".3rem" mt="2">
                      <Text
                        fontWeight="semibold"
                        fontSize={{ base: 'lg', md: '2xl' }}
                      >
                        {t('talkTo')}
                      </Text>
                      <Text
                        fontFamily="Blenda Script"
                        fontSize={{ base: 'lg', md: '2xl' }}
                      >
                        {t('noor')}
                      </Text>
                    </Flex>
                  </Button>
                </Center>
              </Flex>
            </Flex>
          )}
        </Flex>

        {messages.length !== 0 && (
          <>
            <Flex
              pos="absolute"
              bottom={{ base: '2.5rem', md: '2.3rem' }}
              flexDir="column"
              gap={2}
              bg="#FAFAFC"
              pb="1rem"
              w="full"
            >
              <Flex gap={2} pt="1rem">
                {/* <Flex
                gap={2}
                alignItems="center"
                py="2"
                px="4"
                color="#0FA452"
                fontWeight="bold"
                borderRadius="md"
                bg="white"
                border="1px solid #D1DADF"
                fontSize="0.8rem"
              >
                <Image src={SummariseIcon} h="15px" />
                <Box>Summarize</Box>
              </Flex>
              <Flex
                gap={2}
                alignItems="center"
                py="2"
                px="4"
                color="#0FA452"
                fontWeight="bold"
                borderRadius="md"
                bg="white"
                border="1px solid #D1DADF"
                fontSize="0.8rem"
              >
                <Image src={TranslateIcon} h="20px" />
                <Box>Translate</Box>
              </Flex> */}
                <Button
                  bg="linear-gradient(90deg, rgba(107, 35, 193, 0.05) 0%, rgba(213, 17, 104, 0.05) 50%, rgba(243, 153, 63, 0.05) 100%)"
                  gap={2}
                  w="auto"
                  h="2.7rem"
                  _hover={{
                    opacity: '0.9',
                  }}
                  mb="0.45rem"
                  onClick={() => {
                    openVoiceChat();
                    setSelectedVoice('3');
                  }}
                  rounded="lg"
                >
                  <Image src={AdelIcon} h="1.7rem" />
                  <Flex gap=".3rem">
                    <Text
                      fontWeight="semibold"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    >
                      {t('talkTo')}
                    </Text>
                    <Text
                      fontFamily="Blenda Script"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    >
                      {t('adel')}
                    </Text>
                  </Flex>
                </Button>
                <Button
                  bg="linear-gradient(90deg, rgba(107, 35, 193, 0.05) 0%, rgba(213, 17, 104, 0.05) 50%, rgba(243, 153, 63, 0.05) 100%)"
                  gap={2}
                  w="auto"
                  h="2.7rem"
                  _hover={{
                    opacity: '0.9',
                  }}
                  mb="0.45rem"
                  onClick={() => {
                    openVoiceChat();
                    setSelectedVoice('5');
                  }}
                  rounded="lg"
                >
                  <Image src={NoorIcon} h="1.7rem" />
                  <Flex gap=".3rem">
                    <Text
                      fontWeight="semibold"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    >
                      {t('talkTo')}
                    </Text>
                    <Text
                      fontFamily="Blenda Script"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    >
                      {t('noor')}
                    </Text>
                  </Flex>
                </Button>
              </Flex>
            </Flex>
          </>
        )}
        {/* Input area */}
        <Flex mt={2} position="relative">
          {/* Record the voice on click of this button and append the respond to the input field */}
          {isListening ? (
            <Button
              _hover={{
                bg: 'none',
              }}
              variant="ghost"
              p="0"
              onClick={stopListening}
              border="1px solid #d8dfe4"
              bg="white"
              minW="48px"
              h="48px"
              mt="4px"
              me="1"
              style={{
                userSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                KhtmlUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
              zIndex={1}
            >
              <PauseIcon />
            </Button>
          ) : (
            <Button
              _hover={{
                bg: 'none',
              }}
              variant="ghost"
              p="0"
              bg="white"
              border="1px solid #d8dfe4"
              minW="48px"
              h="48px"
              mt="4px"
              me="1"
              onClick={startListening}
              style={{
                userSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                KhtmlUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
              zIndex={1}
            >
              <Image src={MicIcon} w="30px" />
            </Button>
          )}

          <form
            className="flex w-full"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage(conversationId, inputValue);

              setMessages([
                ...messages,
                {
                  role: 0,
                  message: inputValue,
                  timestamp: new Date().toISOString(),
                },
              ]);
              setInputValue('');
              setHideLanguageSwitch(true);
            }}
          >
            <Input
              height="48px"
              me="3.5rem"
              mt="4px"
              fontSize="14px"
              placeholder={
                isSTTLoading ? t('loading') : isListening ? '' : t('askByText')
              }
              _placeholder={{ color: '#353535' }}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              ms={1}
              borderRadius="md"
              borderColor="#DDE5E980"
              px="3"
              isDisabled={isListening || isSTTLoading}
              bg="linear-gradient(90.94deg, #fff5e6 5.73%, #feedff 45.31%, #F5E6FF 99.31%)"
              style={{
                userSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                KhtmlUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
            />
          </form>
          {isListening && (
            <Box
              position="absolute"
              left={language === 'EN' ? '4.5rem' : 'unset'}
              right={language === 'AR' ? '4.5rem' : 'unset'}
              top="1.4rem"
            >
              <VoiceWaveForm
                boxSize={0.2}
                bgColor="linear-gradient(90deg, #6A23C1 0%, #D40D69 50%, #F39A3E 100%)"
              />
            </Box>
          )}
          {/* Record the voice on click of this button and append the respond to the input field */}
          <Button
            _hover={{
              bg: 'none',
            }}
            variant="ghost"
            p="0"
            right={language === 'EN' ? '.5rem' : 'unset'}
            left={language === 'AR' ? '.5rem' : 'unset'}
            transform={language === 'AR' ? 'scaleX(-1)' : 'none'}
            position="absolute"
            top="0.5rem"
            onClick={() => {
              handleSendMessage(conversationId, inputValue);
              setMessages([
                ...messages,
                {
                  role: 0,
                  message: inputValue,
                  timestamp: new Date().toISOString(),
                },
              ]);
              setInputValue('');
              setHideLanguageSwitch(true);
            }}
            style={{
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              KhtmlUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
            zIndex={1}
          >
            <Image src={SendIcon} w="2.5rem" />
          </Button>

          {/* <Button
            p="0"
            variant="ghost"
            _hover={{
              bg: 'none',
            }}
            mt="2"
            style={{
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              KhtmlUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
            onClick={() => {}}
            ms="1"
          >
            <Image src={PromptsIcon} w="53px" />
          </Button> */}
        </Flex>
      </Box>
    </Flex>
  );
};

export default OpenAI;
